import UpgradeTrack, {
  colorByTrack,
  upgradeTracks,
  upgradeTrackToDifficulty,
} from 'app/frontend/types/custom/UpgradeTrack'
import { ColumnDefWithFilters, ColumnGroup } from '../types'
import { useSimpleColumns } from '../utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import CrestCapTooltip from 'app/frontend/base/components/CrestCapTooltip'
import { crestTypes } from 'app/frontend/types/custom/CrestType'
import {
  itemEquipmentSlots,
  itemSlotLabels,
} from 'app/frontend/types/custom/ItemSlot'
import AuditFilter, { useAuditFilterState } from '../components/AuditFilter'
import { MultiSelect } from '@/components/ui/multi-select'
import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { useTeam } from 'app/frontend/base/config/context/team'
import { Badge } from '@/components/ui/badge'
import { crestEarnableCap } from 'app/frontend/base/helpers/crestCap'

export const useGearUpgradesColumns = (): ColumnDefWithFilters => {
  const { value: enabledTracks, setValue: setEnabledTracks } =
    useAuditFilterState<UpgradeTrack[]>('upgradeTracks', [], upgradeTracks)

  const { guild } = useTeam()
  const {
    data: { currentPeriod, currentSeason },
  } = usePageInfo()
  const period = currentPeriod[guild?.realm.region ?? 'EU']

  const collectionGroups: ColumnGroup[] = useMemo(
    () => [
      {
        id: 'items_by_track',
        header: 'Equipped items by track',
        rankFields: null,
        subColumns: upgradeTracks
          .filter((track) => track !== 'None')
          .map((track) => ({
            id: track as string,
            header: () => (
              <Tooltip>
                <TooltipTrigger>{track.charAt(0)}</TooltipTrigger>
                <TooltipContent>{track} track</TooltipContent>
              </Tooltip>
            ),
            field: `${upgradeTrackToDifficulty[track]}_track_items`,
            cellWrapper: (value) =>
              value === '-' ? (
                ''
              ) : (
                <span className={colorByTrack[track]}>{value}</span>
              ),
          })),
      },
      {
        id: 'crests_earned',
        header: 'Crests earned',
        rankFields: [
          'myth_crests',
          'hero_crests',
          'champion_crests',
          'veteran_crests',
        ],
        rankWeights: {
          myth_crests: 1000,
          hero_crests: 10,
          champion_crests: 2,
          veteran_crests: 1,
        },
        subColumns: crestTypes.map((crest) => {
          const seasonCrest = currentSeason.crests.find((c) => c.name === crest)
          const showCap =
            !currentSeason.crests_uncapped &&
            !!seasonCrest?.uncapped_crests &&
            Object.keys(seasonCrest.uncapped_crests).length > 0
          const cap = seasonCrest ? crestEarnableCap(seasonCrest, period) : 0

          return {
            id: `${crest}_crests`,
            header: showCap
              ? () => (
                  <span className='flex flex-col items-center gap-1 pb-1'>
                    <span>{crest}</span>
                    <Badge
                      variant='outline'
                      className='px-1.5 py-0 font-normal whitespace-nowrap'
                    >
                      Cap: {cap}
                    </Badge>
                  </span>
                )
              : crest,
            field: `${crest.toLowerCase()}_crests`,
            cellWrapper: (value, member) => {
              if (value === '-' || value === '' || value == null) {
                return <span>{value}</span>
              }

              const earned = Number(value)
              const earnedSources =
                member.uncappedCrests?.[String(currentSeason.id)]?.[crest] ?? {}
              const className =
                showCap && earned >= cap
                  ? 'text-success-foreground'
                  : showCap
                  ? 'text-warning-foreground'
                  : undefined

              if (
                currentSeason.crests_uncapped ||
                !seasonCrest ||
                earned >= cap
              ) {
                return <span className={className}>{value}</span>
              }

              return (
                <CrestCapTooltip
                  crest={seasonCrest}
                  period={period}
                  earned={earned}
                  earnedSources={earnedSources}
                  className={cn('whitespace-nowrap', className)}
                >
                  {value}
                </CrestCapTooltip>
              )
            },
          }
        }),
      },
      {
        id: 'upgrades_missing',
        header: 'Upgrades',
        rankInverted: true,
        subColumns: [
          {
            id: 'total_upgrades_missing',
            header: 'Missing',
            field: 'total_upgrades_missing',
          },
        ],
      },
      {
        id: 'upgrades_by_slot',
        header: 'Upgrade steps',
        rankFields: null,
        subColumns: itemEquipmentSlots.map((slot) => ({
          id: slot as string,
          header: () => <span>{itemSlotLabels[slot]}</span>,
          field: `upgrade_level_${slot}`,
          cellWrapper: (value, member) => {
            const [step, total] = String(value).split('/').map(Number)
            const track = member.gear?.[slot]?.upgrade_level?.track

            const valueToShow =
              enabledTracks.length === 0
                ? value
                : enabledTracks.includes(track as UpgradeTrack)
                ? value
                : ''

            return (
              <Tooltip>
                <TooltipTrigger
                  className={cn(
                    'whitespace-nowrap',
                    valueToShow === '-'
                      ? ''
                      : step === total
                      ? 'text-success-foreground'
                      : 'text-destructive-foreground',
                  )}
                >
                  {valueToShow}
                </TooltipTrigger>
                {track === 'None' ? null : (
                  <TooltipContent>
                    {track ? (
                      <span className={colorByTrack[track]}>{track}</span>
                    ) : (
                      'Unknown track'
                    )}
                  </TooltipContent>
                )}
              </Tooltip>
            )
          },
        })),
      },
    ],
    [currentSeason, currentPeriod, enabledTracks, period],
  )

  const columns = useSimpleColumns(collectionGroups, [enabledTracks])

  return {
    columns,
    initialSortBy: [{ id: 'Myth_crests', desc: true }],
    filters: [
      <AuditFilter label='Count missing steps from' key='track'>
        <MultiSelect<UpgradeTrack>
          options={upgradeTracks.slice(0, 4).map((track) => ({
            value: track,
            label: <span className={colorByTrack[track]}>{track}</span>,
          }))}
          placeholder='All tracks'
          value={enabledTracks}
          onChange={(value) => setEnabledTracks(value)}
        />
      </AuditFilter>,
    ],
  }
}
